import { Component, inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { PaymentCardFormComponent } from '@components/consultation-request/payment-card-form/payment-card-form.component';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { ConsultationRequestHeaderComponent } from '@components/consultation-request-header/consultation-request-header.component';
import { ProcessingWarningNotification } from '@components/processing-warning-notification/processing-warning-notification';
import { environment } from '@environments/environment';
import { InternationalCreditCardNotSupportedError } from '@errors/international-credit-card-not-supported-error';
import { ConfigService as PaymentConfigService, Payment } from '@medology/ngx-payment';
import { OrderUpsell } from '@models/order-upsell';
import { ConsultationResponse } from '@models/responses/consultation-response.model';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { OrderPaymentService } from '@services/order-payment.service';
import { PaymentService, provideDefaultPaymentConfig } from '@services/payment.service';
import { TreatmentConsultationQuestionnaireService } from '@services/treatment-consultation-questionnaire.service';
import { TreatmentConsultationSubmissionService } from '@services/treatment-consultation-submission.service';
import { catchError, finalize, Observable, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-consultation-upsell-payment-card',
  templateUrl: './consultation-upsell-payment-card.component.html',
  styleUrl: './consultation-upsell-payment-card.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    ConsultationRequestHeaderComponent,
    PaymentCardFormComponent,
    PaymentSubmitButtonComponent,
    ProcessingWarningNotification,
  ],
  providers: [provideDefaultPaymentConfig()],
})
export class ConsultationUpsellPaymentCardComponent implements OnInit {
  @ViewChild('paymentCardFormComponent', { static: false }) public paymentCardFormComponent: PaymentCardFormComponent;

  public processing: boolean = false;
  public payments: Payment[] = [];
  public submissionErrors: string[] = [];

  private paymentConfigService = inject(PaymentConfigService);
  private errorHandlerService = inject(ErrorHandlerService);
  private navigationService = inject(NavigationService);
  private orderService = inject(OrderService);
  private consultationStorageService = inject(ConsultationStorageService);
  private treatmentConsultationQuestionnaireService = inject(TreatmentConsultationQuestionnaireService);
  private treatmentConsultationSubmissionService = inject(TreatmentConsultationSubmissionService);
  private orderPaymentService = inject(OrderPaymentService);
  private paymentService = inject(PaymentService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Gets the line items to display on the payment form, including upsells and any in-home collection charge.
   *
   * @returns {OrderUpsell[]} the line items array
   */
  public get lineItems(): OrderUpsell[] {
    const inHomeCollection = this.buildInHomeCollectionLineItem();

    return inHomeCollection ? [...this.getUpsells(), inHomeCollection] : this.getUpsells();
  }

  /**
   * Gets the previous balance in cents from local storage, defaulting to 0.
   *
   * @returns {number} The previous balance in cents.
   */
  public get previousBalanceInCents(): number {
    return Math.max(this.consultationStorageService.remainingBalance, 0);
  }

  public ngOnInit(): void {
    this.updatePaymentConfig();
  }

  /**
   * Handles payment selection from the payment form.
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.submissionErrors = [];
  }

  /**
   * Submits the upsell payment and consultation request.
   */
  public submit(): void {
    this.submissionErrors = [];

    if (!this.paymentCardFormComponent?.validateForm()) {
      return;
    }

    const isExternalSubscription = this.consultationRequestService.isExternalSubscription;

    if (isExternalSubscription && this.paymentService.hasUnsupportedCreditCard(this.payments)) {
      this.submissionErrors = [new InternationalCreditCardNotSupportedError(environment.phone).message];

      return;
    }

    this.processing = true;

    const submission = isExternalSubscription ? this.submitForExternalSubscription() : this.submitForOrderPayment();

    submission.pipe(finalize(() => (this.processing = false))).subscribe({
      next: (consultationResponse) =>
        this.navigationService.handlePostConsultationUpdateNavigation(
          consultationResponse?.schedule_once_id || this.consultationStorageService.scheduleOnceId,
        ),
      error: (error) => (this.submissionErrors = this.errorHandlerService.handleConsultationSubmissionError(error)),
    });
  }

  /**
   * Charges the selected payment methods to the order and submits the consultation request.
   *
   * @returns {Observable<ConsultationResponse>} the consultation update response
   */
  private submitForOrderPayment(): Observable<ConsultationResponse> {
    const [initialRequest, followUpRequest] = this.paymentService.getPaymentRequests(
      this.payments,
      this.getSubmitTotal(),
      this.consultationStorageService.transactionId,
      this.consultationStorageService.patient.name,
    );

    return this.orderPaymentService.store(initialRequest, this.consultationStorageService.hash).pipe(
      tap((response) => (this.consultationStorageService.upsell = [response.transaction_id])),
      switchMap(() =>
        followUpRequest
          ? this.orderPaymentService
              .store(followUpRequest, this.consultationStorageService.hash)
              .pipe(catchError((error) => this.orderService.swallowError(error)))
          : of(null),
      ),
      switchMap(() => this.treatmentConsultationSubmissionService.submit()),
    );
  }

  /**
   * Saves the selected payment method on the order's subscription, then submits the upsells and the consultation
   * request.
   *
   * @returns {Observable<ConsultationResponse>} the consultation update response
   */
  private submitForExternalSubscription(): Observable<ConsultationResponse> {
    const orderId = this.consultationStorageService.transactionId;
    const hash = this.consultationStorageService.hash;

    return this.consultationRequestService
      .updateSubscriptionBilling(this.paymentService.getSubscriptionBillingUpdateRequest(this.payments), orderId, hash)
      .pipe(
        switchMap(() =>
          this.orderService.submitUpsells(
            this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice(),
            orderId,
            hash,
          ),
        ),
        tap(() => (this.consultationStorageService.upsell = [orderId])),
        switchMap(() => this.treatmentConsultationSubmissionService.submit({ skipUpsellSubmission: true })),
      );
  }

  /**
   * Calculates the total amount to be submitted, including upsell prices and any remaining balance.
   *
   * @returns {number} The total amount in cents.
   */
  private getSubmitTotal(): number {
    const orderUpsells = this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice();
    const upsellChargeAmount = orderUpsells.reduce((acc, upsell) => acc + upsell.upsell_price, 0);

    return upsellChargeAmount + this.previousBalanceInCents + this.orderService.getInHomeCollectionTotal();
  }

  /**
   * Updates the to the ngx-payment payment config during init.
   */
  private updatePaymentConfig(): void {
    this.paymentService.setGiftCardConfig(this.paymentConfigService);
  }

  /**
   * Builds the OrderUpsell line item for in-home collection if in home collection was selected.
   */
  private buildInHomeCollectionLineItem(): OrderUpsell | null {
    const total = this.orderService.getInHomeCollectionTotal();

    return total <= 0
      ? null
      : {
          id: null,
          slug: 'in-home-collection',
          name: 'In-Home Collection',
          price: total,
          upsell_price: total,
        };
  }

  /**
   * Gets the upsells array with the price.
   *
   * @returns {OrderUpsell[]} the upsells array
   */
  private getUpsells(): OrderUpsell[] {
    return this.treatmentConsultationQuestionnaireService.getUpsellsWithPrice().filter((upsell) => upsell.upsell_price);
  }
}
